/**
 * Breaking News Detection — identifies high-priority breaking news events.
 *
 * Criteria:
 * 1. Recency gate: event must be from last 15 minutes
 * 2. Severity filter: critical or high severity only
 * 3. Per-source cooldown: max 1 breaking alert per source per 30 minutes
 * 4. Global cooldown: max 1 breaking alert globally per 60 seconds
 * 5. Authority tier boosting: Tier 1 (wire) events get priority
 *
 * Emits BreakingNewsAlert events for the banner component and notification system.
 */

import type { NormalizedEvent, Severity } from '@/sources/base-source';
import { getEventTimestamp } from './types';

export interface BreakingNewsAlert {
  eventId: string;
  title: string;
  source: string;
  severity: Severity;
  timestamp: string;
  authorityTier: number;
  detectedAt: string;
}

const RECENCY_GATE_MS = 60 * 60 * 1000;      // 60 minutes (RSS feeds have delayed timestamps)
const SOURCE_COOLDOWN_MS = 30 * 60 * 1000;    // 30 minutes per source
const GLOBAL_COOLDOWN_MS = 60 * 1000;         // 60 seconds global
const VALID_SEVERITIES: Set<Severity> = new Set(['critical', 'high']);

/** Tracks the last time a breaking alert was emitted per source and globally. */
const lastAlertBySource: Map<string, number> = new Map();
let lastGlobalAlert = 0;

/** Known wire service source IDs for authority boosting. */
const WIRE_SOURCES: Set<string> = new Set([
  'reuters', 'ap', 'afp', 'bloomberg', 'gdelt',
]);

/** Event types that are NOT intelligence-relevant for breaking news.
 *  Weather, fires, earthquakes, and routine sensor data are noise — not OSINT. */
const BREAKING_EXCLUDED_TYPES = new Set([
  'weather', 'fire', 'earthquake', 'disaster', 'nasa_event',
  'vessel_position', 'flight_position', 'economic', 'space',
]);

/** Sources that produce non-intelligence alerts (weather services, sensor feeds). */
const BREAKING_EXCLUDED_SOURCES = new Set([
  'weather', 'firms', 'usgs', 'gdacs', 'eonet', 'opensky',
  'vesselfinder', 'fred', 'polymarket',
]);

/**
 * Detect breaking news events from the current event set.
 * Returns alerts that pass all gates and cooldowns.
 */
export function detectBreakingNews(events: NormalizedEvent[]): BreakingNewsAlert[] {
  const now = Date.now();
  const recencyCutoff = now - RECENCY_GATE_MS;
  const alerts: BreakingNewsAlert[] = [];

  // Sort by timestamp descending (newest first) to prioritize recent events
  const candidates = events
    .filter(ev => {
      const ts = getEventTimestamp(ev);
      if (ts < recencyCutoff) return false;
      if (!VALID_SEVERITIES.has(ev.severity ?? 'info')) return false;
      // Filter out non-intelligence events
      if (BREAKING_EXCLUDED_TYPES.has(ev.type)) return false;
      if (BREAKING_EXCLUDED_SOURCES.has(ev.source)) return false;
      // Reject pipeline artifacts: titles that are raw JSON or empty
      const title = ev.title?.trim();
      if (!title || title.startsWith('{') || title.startsWith('[')) return false;
      // Hide untranslated foreign-language content
      if (ev.language && ev.language !== 'en' && ev.language !== 'unknown' && !ev.tags?.includes('translated')) return false;
      return true;
    })
    .sort((a, b) => getEventTimestamp(b) - getEventTimestamp(a));

  for (const ev of candidates) {
    // Global cooldown check (critical events bypass)
    if (now - lastGlobalAlert < GLOBAL_COOLDOWN_MS && ev.severity !== 'critical') continue;

    // Per-source cooldown check
    const lastSourceAlert = lastAlertBySource.get(ev.source) ?? 0;
    if (now - lastSourceAlert < SOURCE_COOLDOWN_MS) continue;

    // Authority tier: wire services get tier 1, others get tier 3
    const authorityTier = WIRE_SOURCES.has(ev.source) ? 1 : 3;

    alerts.push({
      eventId: ev.id,
      title: ev.title,
      source: ev.source,
      severity: ev.severity ?? 'high',
      timestamp: ev.timestamp,
      authorityTier,
      detectedAt: new Date(now).toISOString(),
    });

    // Update cooldowns
    lastAlertBySource.set(ev.source, now);
    lastGlobalAlert = now;
  }

  // Prune stale cooldown entries
  if (lastAlertBySource.size > 100) {
    const pruneCutoff = now - SOURCE_COOLDOWN_MS * 2;
    for (const [source, ts] of lastAlertBySource) {
      if (ts < pruneCutoff) lastAlertBySource.delete(source);
    }
  }

  return alerts;
}

/** Reset all cooldowns (for testing). */
export function resetBreakingNewsCooldowns(): void {
  lastAlertBySource.clear();
  lastGlobalAlert = 0;
}
